import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollManager — handles scroll position across React Router navigation.
 *
 * Rules:
 *  PUSH   (user clicks a <Link> to a new page) → scroll to top
 *  REPLACE (programmatic navigation, redirects)  → scroll to top
 *  POP    (browser Back / Forward button)        → do nothing;
 *                                                   the browser restores the
 *                                                   saved position natively
 *  Hash navigation (anchor links like #section) → do nothing;
 *                                                   the browser scrolls to the
 *                                                   target element
 *
 * Must be rendered inside a <Router> context (e.g. inside AppRouter).
 * Returns null — no DOM output.
 */
export default function ScrollManager() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'

  useEffect(() => {
    // Back / Forward — let the browser restore the saved scroll position.
    if (navigationType === 'POP') return;

    // Anchor links — let the browser scroll to the target element.
    if (hash) return;

    // New page navigation (PUSH) or redirect (REPLACE) — go to top.
    // Respect the user's OS-level reduced-motion preference.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    });
  }, [pathname, hash, navigationType]);

  return null;
}
